<div class="container-fluid quote-analytics-page">
  <p-toast [style]="{marginTop: '80px'}"></p-toast>

  @if (formLoading) {
    <div class="progress-spinner">
      <app-spinner [formLoading]="formLoading"></app-spinner>
    </div>
  }

  <h2>Sales Pipeline Analytics</h2>
  <p class="subtitle">End-to-end sales pipeline — quotes, holds, and upsell extension offers. Tracks every channel from sent through booked, with KPIs, trends, and drill-downs by property, reservationist, source, and more.</p>

  <!-- Filter row -->
  <!-- Two independent date windows:
       • Quote Create Start/End → scopes quote-side metrics (Quotes Sent, Quoted Value, Pending, Expired, etc.)
       • Sale Date Start/End → scopes booking-side metrics (Booked count, Booked Value, Time-to-Book)
       This way a quote sent 3 days ago that closed today is still counted under today's Booked window. -->
  <app-search-template
    searchLabel="Search"
    [showSearchButton]=true
    [reset]=true
    [showLocations]=true
    [showDevelopments]=true
    [showShortTermRentals]=true
    [showBedrooms]=true
    [showFilterDateStart]=true
    [showFilterDateEnd]=true
    [showSaleDateStart]=true
    [showSaleDateEnd]=true
    [filterDateStartLabel]="'Quote Create Start'"
    [filterDateEndLabel]="'Quote Create End'"
    [saleDateStartLabel]="'Start Date'"
    [saleDateEndLabel]="'End Date'"
    [saleDateStart]="defaultSaleDate"
    [saleDateEnd]="defaultSaleDate"
    [disableFilterDateDefault]="true"
    [displayRecordsOnPageLoad]="true"
    (doSearchCB)="onSearchSubmit($event)">
  </app-search-template>

  <br>

  @if (data && data.kpis) {

    <!-- Period banner -->
    <!-- Banner names the filter the user actually picked, in plain English,
         so the team doesn't have to learn new jargon:
           • Sale Date filter   → "Shown by Sale Date" (each metric uses its
                                  own event date — created / booked / expired
                                  / replied all use their natural date column).
           • Quote-Create only  → "Shown by Quote-Created Date" (all metrics
                                  scope to when the quote was created; bookings,
                                  expirations, etc. are outcomes of that group). -->
    <div class="period-banner" [class.activity-mode]="data.activityMode">
      @if (data.activityMode) {
        <div>
          <span class="mode-tag">SHOWN BY SALE DATE</span>
          <strong>{{ data.periodStart | date:'MMM d, y' }}</strong>
          <span> – </span>
          <strong>{{ data.periodEnd | date:'MMM d, y' }}</strong>
          <span class="vs"> · every number counts what actually happened in this date range (quotes created here, bookings closed here, expirations here, replies here)</span>
        </div>
      } @else {
        <div>
          <span class="mode-tag">SHOWN BY QUOTE-CREATED DATE</span>
          <strong>{{ data.periodStart | date:'MMM d, y' }}</strong>
          <span> – </span>
          <strong>{{ data.periodEnd | date:'MMM d, y' }}</strong>
          <span class="vs"> · all numbers cover quotes created in this date range — bookings/expirations/etc. are outcomes of those quotes, even if they happened later</span>
        </div>
      }
    </div>

    <!-- ════ RES SALES KPIs — mirrors Res Sales Dashboard headline tiles ════ -->
    @if (data.resKpis) {
    <div class="section-header" style="border-bottom:2px solid #0f766e;margin-bottom:12px">
      <h4 style="color:#0f766e">Reservation Revenue
        <i class="pi pi-info-circle info-icon"
           pTooltip="Confirmed reservation (OP/OG) totals for the selected date window, scoped by sale date — the same figures shown on the Res Sales Dashboard. Nightly rate revenue sourced from the reservation ledger."
           tooltipPosition="top"></i>
      </h4>
      <p class="section-desc">Actual earned revenue for the period — Owner Amount + KBM Management Fee (tax excluded). Not quotes or holds — only confirmed reservations with a sale date in the selected window.</p>
    </div>
    <div class="kpi-grid" style="grid-template-columns:repeat(5,minmax(140px,200px));justify-content:start">
      <div class="kpi-tile booked" style="cursor:pointer" (click)="drillDownRes(null, 'All Reservations')">
        <div class="kpi-label">Total Room Rate
          <i class="pi pi-info-circle info-icon"
             pTooltip="Owner Amount + KBM Management Fee (excluding tax) for all confirmed reservations in the window. This is the actual revenue earned — the same calculation used in the Res Sales Dashboard."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.totalRoomRate) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.reservations) }} reservation{{ data.resKpis.reservations === 1 ? '' : 's' }}</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-label">Avg Room Rate
          <i class="pi pi-info-circle info-icon"
             pTooltip="Total Room Rate ÷ confirmed reservations. Average revenue per reservation in the window."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.avgRoomRate) }}</div>
        <div class="kpi-sub">per reservation</div>
      </div>
      <div class="kpi-tile" style="cursor:pointer" (click)="drillDownRes(null, 'All Reservations')">
        <div class="kpi-label">Reservations
          <i class="pi pi-info-circle info-icon"
             pTooltip="Guest (OP) reservations + paying Owner Guest (OG) reservations in the window. Non-paying owner guest stays (Owner Amount + KBM Fee = $0) are excluded from count, total, and average."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatNumber(data.resKpis.reservations) }}</div>
        <div class="kpi-sub">confirmed bookings</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-label">Total Nights
          <i class="pi pi-info-circle info-icon"
             pTooltip="Sum of numberOfNightsStay across all qualifying reservations in the window. Same scope as Reservations count — Guest + paying Owner Guest."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.resKpis.totalNights) }}</div>
        <div class="kpi-sub">nights stayed</div>
      </div>
      <div class="kpi-tile">
        <div class="kpi-label">Avg Nights
          <i class="pi pi-info-circle info-icon"
             pTooltip="Total Nights ÷ confirmed reservations. Average length of stay for the window."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ data.resKpis.avgNights | number:'1.1-1' }}</div>
        <div class="kpi-sub">avg length of stay</div>
      </div>
    </div>

    <!-- ── Row 2 — 12 source breakdown tiles (2 per source × 6 sources) ─── -->
    <div class="kpi-grid kpi-grid-tertiary" style="grid-template-columns:repeat(12,minmax(100px,1fr));margin-top:8px">

      <!-- Website -->
      <div class="kpi-tile small res-guest" style="cursor:pointer" (click)="drillDownRes('website', 'Website')">
        <div class="kpi-label">Website Revenue
          <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for direct website bookings (referralSourceContext = Website)." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.website.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.website.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-guest">
        <div class="kpi-label">Website Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for website bookings = Website Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.website.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

      <!-- From Hold -->
      <div class="kpi-tile small res-hold" style="cursor:pointer" (click)="drillDownRes('hold', 'Hold')">
        <div class="kpi-label">Hold Revenue
          <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for bookings that came through a hold (referralSourceContext = Hold)." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.fromHold.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.fromHold.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-hold">
        <div class="kpi-label">Hold Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for hold-origin bookings = Hold Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.fromHold.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

      <!-- OTA -->
      <div class="kpi-tile small res-guest" style="cursor:pointer" (click)="drillDownRes('ota', 'OTA')">
        <div class="kpi-label">OTA Revenue
          <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for OTA bookings (Airbnb, VRBO, Booking.com, Expedia, etc.)." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.ota.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.ota.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-guest">
        <div class="kpi-label">OTA Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for OTA bookings = OTA Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.ota.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

      <!-- From Quote -->
      <div class="kpi-tile small res-quote" style="cursor:pointer" (click)="drillDownRes('quote', 'Quote')">
        <div class="kpi-label">Quote Revenue
          <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for bookings originated from a sent quote (referralSourceContext = Quote)." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.fromQuote.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.fromQuote.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-quote">
        <div class="kpi-label">Quote Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for quote-origin bookings = Quote Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.fromQuote.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

      <!-- Owner Guest paying -->
      <div class="kpi-tile small res-og" style="cursor:pointer" (click)="drillDownRes('og', 'Owner Guest')">
        <div class="kpi-label">Owner Guest Rev.
          <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for paying owner-guest reservations (OG status with revenue > $0)." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.ownerGuest.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.ownerGuest.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-og">
        <div class="kpi-label">Owner Guest Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for paying owner-guest stays = Owner Guest Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.ownerGuest.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

      <!-- Manual / Other — phone/staff-entered, no website/hold/quote/OTA source -->
      <div class="kpi-tile small res-guest" style="cursor:pointer" (click)="drillDownRes('manual', 'Manual / Other')">
        <div class="kpi-label">Manual / Other
          <i class="pi pi-info-circle info-icon"
             pTooltip="OP reservations where the source is not Website, Hold, Quote, or any known OTA channel. Typically phone-in or staff-entered bookings (referralSourceContext = Manual or unknown). These are the bookings that would otherwise be unaccounted for in the source breakdown."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link">{{ formatMoney(data.resKpis.manual.totalRevenue) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.resKpis.manual.reservations) }} res</div>
      </div>
      <div class="kpi-tile small res-guest">
        <div class="kpi-label">Manual Avg/Night
          <i class="pi pi-info-circle info-icon" pTooltip="Average nightly rate for manual/other bookings = Manual Revenue ÷ total nights." tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.resKpis.manual.avgNightlyRate) }}</div>
        <div class="kpi-sub">per night</div>
      </div>

    </div>

    <!-- ════ TEAM PERFORMANCE — FULL BREAKDOWN ════ -->
    <!-- Unified per-reservationist table: quote/hold pipeline + reservation revenue by source. -->
    @if (mergedTeamRows.length) {
      <div class="section-header quote-section-header" style="margin-top:20px">
        <h4>⑩ Team Performance — Full Breakdown</h4>
        <p class="section-desc">
          One row per reservationist across all channels.
          <strong>Quotes:</strong> guests quoted in window, Conv% (booked ÷ open — sub-line shows X / Y), Quote revenue.
          <strong>Holds:</strong> holds created, Conv% (confirmed ÷ created — sub-line shows X / Y), Hold revenue.
          <strong>Revenue</strong> (Owner Amt + KBM Fee, ex-tax) by source: Website, OTA, Owner Guest, Manual, Total.
          Sorted by Total Revenue — use column headers to re-sort.
        </p>
      </div>

      <div class="card">
        <div class="card-header">
          <h3>Team Summary
            <i class="pi pi-info-circle info-icon"
               pTooltip="Quotes group: guests quoted &amp; Conv% (booked ÷ open right now) scoped by Quote-Create window. Holds group: holds created &amp; Conv% (confirmed ÷ created) scoped by Sale Date window. Revenue columns use the confirmed reservation ledger scoped by Sale Date. A reservationist may appear with pipeline activity but $0 revenue if their bookings fall outside the sale-date window."
               tooltipPosition="top"></i>
          </h3>
          <span class="card-header-note">Revenue = Owner Amt + KBM Fee (ex-tax) · count shown below each $</span>
        </div>
        <p-table #ptTeam [value]="mergedTeamRows"
          styleClass="p-datatable-striped p-datatable-sm"
          [scrollable]="true" scrollHeight="520px"
          sortMode="single" [defaultSortField]="'totalRevenue'" [defaultSortOrder]="-1">
          <ng-template pTemplate="header">
            <!-- ── Group header row ─────────────────────────────────────── -->
            <tr>
              <th style="min-width:140px;border-bottom:none;background:#f9fafb"></th>
              <!-- Quotes group -->
              <th colspan="3"
                  style="text-align:center;font-size:11px;font-weight:700;letter-spacing:.04em;
                         text-transform:uppercase;color:#2563eb;background:#eff6ff;
                         border-left:2px solid #93c5fd;border-bottom:2px solid #93c5fd">
                Quotes
              </th>
              <!-- Holds group -->
              <th colspan="3"
                  style="text-align:center;font-size:11px;font-weight:700;letter-spacing:.04em;
                         text-transform:uppercase;color:#d97706;background:#fffbeb;
                         border-left:2px solid #fcd34d;border-bottom:2px solid #fcd34d">
                Holds
              </th>
              <!-- Revenue by source -->
              <th colspan="5"
                  style="text-align:center;font-size:11px;font-weight:700;letter-spacing:.04em;
                         text-transform:uppercase;color:#059669;background:#ecfdf5;
                         border-left:2px solid #6ee7b7;border-bottom:2px solid #6ee7b7">
                Confirmed Revenue (Owner Amt + KBM Fee, ex-tax)
              </th>
              <!-- Total -->
              <th style="border-bottom:2px solid #d1d5db;background:#f9fafb"></th>
            </tr>
            <!-- ── Column sort headers ──────────────────────────────────── -->
            <tr>
              <th pSortableColumn="reservationistName" style="min-width:140px">
                Reservationist <p-sortIcon field="reservationistName"></p-sortIcon>
              </th>
              <!-- Quotes -->
              <th class="num" pSortableColumn="quotesSent"
                  style="border-left:2px solid #93c5fd"
                  pTooltip="Unique guests who received at least one quote in the window" tooltipPosition="top">
                Guests Quoted <p-sortIcon field="quotesSent"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="quoteConversionRate"
                  pTooltip="Guests booked (in window) ÷ guests with an open quote right now. Sub-line shows booked / open." tooltipPosition="top">
                Conv% <p-sortIcon field="quoteConversionRate"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="quoteRevenue"
                  pTooltip="Revenue from quote-origin bookings (referralSourceContext = Quote)" tooltipPosition="top">
                Quote $ <p-sortIcon field="quoteRevenue"></p-sortIcon>
              </th>
              <!-- Holds -->
              <th class="num" pSortableColumn="holdsCreated"
                  style="border-left:2px solid #fcd34d"
                  pTooltip="Holds created in the window" tooltipPosition="top">
                Holds Created <p-sortIcon field="holdsCreated"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="holdConversionRate"
                  pTooltip="Holds confirmed (OP) ÷ holds created. Sub-line shows confirmed / created." tooltipPosition="top">
                Conv% <p-sortIcon field="holdConversionRate"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="holdRevenue"
                  pTooltip="Revenue from hold-origin bookings (referralSourceContext = Hold)" tooltipPosition="top">
                Hold $ <p-sortIcon field="holdRevenue"></p-sortIcon>
              </th>
              <!-- Revenue by source -->
              <th class="num" pSortableColumn="websiteRevenue"
                  style="border-left:2px solid #6ee7b7"
                  pTooltip="Revenue from direct website bookings" tooltipPosition="top">
                Website $ <p-sortIcon field="websiteRevenue"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="otaRevenue"
                  pTooltip="Revenue from OTA channels (Airbnb, VRBO, Booking.com, Expedia, etc.)" tooltipPosition="top">
                OTA $ <p-sortIcon field="otaRevenue"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="ogRevenue"
                  pTooltip="Revenue from paying owner-guest stays (OG with revenue &gt; $0)" tooltipPosition="top">
                Owner Guest $ <p-sortIcon field="ogRevenue"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="manualRevenue"
                  pTooltip="Revenue from manual/other bookings — phone-in or staff-entered, not matched to Website, Hold, OTA, or Quote source" tooltipPosition="top">
                Manual $ <p-sortIcon field="manualRevenue"></p-sortIcon>
              </th>
              <!-- Total -->
              <th class="num" pSortableColumn="totalRevenue"
                  pTooltip="Total confirmed reservation revenue across all sources" tooltipPosition="top">
                Total $ <p-sortIcon field="totalRevenue"></p-sortIcon>
              </th>
            </tr>
            <tr class="filter-row">
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptTeam.filter($any($event.target).value, 'reservationistName', 'contains')"></th>
              <th style="border-left:2px solid #93c5fd"></th><th></th><th></th>
              <th style="border-left:2px solid #fcd34d"></th><th></th><th></th>
              <th style="border-left:2px solid #6ee7b7"></th><th></th><th></th><th></th>
              <th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.reservationistName || '(Unassigned)' }}</td>
              <!-- Quotes -->
              <td class="num" style="border-left:2px solid #93c5fd">{{ row.quotesSent > 0 ? formatNumber(row.quotesSent) : '—' }}</td>
              <td class="num">
                {{ row.activeQuotes > 0 ? formatPct(row.quoteConversionRate) : '—' }}
                @if (row.activeQuotes > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ row.guestsBooked }} / {{ row.activeQuotes }} open</div>
                }
              </td>
              <td class="num">
                {{ row.quoteRevenue > 0 ? formatMoney(row.quoteRevenue) : '—' }}
                @if (row.quoteCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.quoteCount) }} res</div>
                }
              </td>
              <!-- Holds -->
              <td class="num" style="border-left:2px solid #fcd34d">{{ row.holdsCreated > 0 ? formatNumber(row.holdsCreated) : '—' }}</td>
              <td class="num">
                {{ row.holdsCreated > 0 ? formatPct(row.holdConversionRate) : '—' }}
                @if (row.holdsCreated > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ row.holdsConfirmed }} / {{ row.holdsCreated }} created</div>
                }
              </td>
              <td class="num">
                {{ row.holdRevenue > 0 ? formatMoney(row.holdRevenue) : '—' }}
                @if (row.holdCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.holdCount) }} res</div>
                }
              </td>
              <!-- Revenue by source -->
              <td class="num" style="border-left:2px solid #6ee7b7">
                {{ row.websiteRevenue > 0 ? formatMoney(row.websiteRevenue) : '—' }}
                @if (row.websiteCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.websiteCount) }} res</div>
                }
              </td>
              <td class="num">
                {{ row.otaRevenue > 0 ? formatMoney(row.otaRevenue) : '—' }}
                @if (row.otaCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.otaCount) }} res</div>
                }
              </td>
              <td class="num">
                {{ row.ogRevenue > 0 ? formatMoney(row.ogRevenue) : '—' }}
                @if (row.ogCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.ogCount) }} res</div>
                }
              </td>
              <td class="num">
                {{ row.manualRevenue > 0 ? formatMoney(row.manualRevenue) : '—' }}
                @if (row.manualCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.manualCount) }} res</div>
                }
              </td>
              <!-- Total -->
              <td class="num">
                <strong>{{ row.totalRevenue > 0 ? formatMoney(row.totalRevenue) : '—' }}</strong>
                @if (row.totalCount > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(row.totalCount) }} res</div>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td style="font-style:italic;color:#6b7280">Total</td>
              <!-- Quotes -->
              <td class="num" style="border-left:2px solid #93c5fd">{{ formatNumber(sumBy(mergedTeamRows, 'quotesSent')) }}</td>
              <td></td>
              <td class="num">
                {{ formatMoney(sumBy(mergedTeamRows, 'quoteRevenue')) }}
                @if (sumBy(mergedTeamRows, 'quoteCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'quoteCount')) }} res</div>
                }
              </td>
              <!-- Holds -->
              <td class="num" style="border-left:2px solid #fcd34d">{{ formatNumber(sumBy(mergedTeamRows, 'holdsCreated')) }}</td>
              <td></td>
              <td class="num">
                {{ formatMoney(sumBy(mergedTeamRows, 'holdRevenue')) }}
                @if (sumBy(mergedTeamRows, 'holdCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'holdCount')) }} res</div>
                }
              </td>
              <!-- Revenue by source -->
              <td class="num" style="border-left:2px solid #6ee7b7">
                {{ formatMoney(sumBy(mergedTeamRows, 'websiteRevenue')) }}
                @if (sumBy(mergedTeamRows, 'websiteCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'websiteCount')) }} res</div>
                }
              </td>
              <td class="num">
                {{ formatMoney(sumBy(mergedTeamRows, 'otaRevenue')) }}
                @if (sumBy(mergedTeamRows, 'otaCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'otaCount')) }} res</div>
                }
              </td>
              <td class="num">
                {{ formatMoney(sumBy(mergedTeamRows, 'ogRevenue')) }}
                @if (sumBy(mergedTeamRows, 'ogCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'ogCount')) }} res</div>
                }
              </td>
              <td class="num">
                {{ formatMoney(sumBy(mergedTeamRows, 'manualRevenue')) }}
                @if (sumBy(mergedTeamRows, 'manualCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'manualCount')) }} res</div>
                }
              </td>
              <!-- Total -->
              <td class="num">
                <strong>{{ formatMoney(sumBy(mergedTeamRows, 'totalRevenue')) }}</strong>
                @if (sumBy(mergedTeamRows, 'totalCount') > 0) {
                  <div class="kpi-sub" style="text-align:right">{{ formatNumber(sumBy(mergedTeamRows, 'totalCount')) }} res</div>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="12" style="text-align:center">No data in window.</td></tr>
          </ng-template>
        </p-table>
      </div>
    }

    <!-- ════ RESERVATION DETAIL DRILL-DOWN ════ -->
    <!-- Always visible within resKpis block. Click a source KPI tile above to filter. -->
    @if (data.resDetails) {
      <div class="card" id="res-detail-anchor" style="margin-top:8px">
        <div class="card-header">
          <h3>Reservation Detail
            ({{ resGroupBy === 'none' ? filteredResDetails.length : groupedResDetails.length }} rows)
            @if (activeResFilter) {
              <span class="drill-badge">
                {{ activeResFilter }}
                <button class="drill-clear" (click)="clearResFilter()" title="Clear filter">✕</button>
              </span>
            }
            <i class="pi pi-info-circle info-icon"
               pTooltip="Row-level view of every confirmed reservation in the window. Click any Reservation Revenue KPI tile above to filter by source. Use the group-by buttons to aggregate by day, month, or year. Capped at 2000 rows."
               tooltipPosition="top"></i>
          </h3>
          <!-- Group-by toggle -->
          <div style="display:flex;align-items:center;gap:4px;flex-shrink:0">
            <span style="font-size:12px;color:#6b7280;margin-right:4px">Group by:</span>
            <button type="button"
                    [style.font-weight]="resGroupBy==='none'?'700':'400'"
                    [style.color]="resGroupBy==='none'?'#2563eb':'#6b7280'"
                    [style.border-bottom]="resGroupBy==='none'?'2px solid #2563eb':'2px solid transparent'"
                    style="background:none;border:none;padding:2px 8px;cursor:pointer;font-size:13px;line-height:1.6"
                    (click)="resGroupBy='none'">None</button>
            <button type="button"
                    [style.font-weight]="resGroupBy==='day'?'700':'400'"
                    [style.color]="resGroupBy==='day'?'#2563eb':'#6b7280'"
                    [style.border-bottom]="resGroupBy==='day'?'2px solid #2563eb':'2px solid transparent'"
                    style="background:none;border:none;padding:2px 8px;cursor:pointer;font-size:13px;line-height:1.6"
                    (click)="resGroupBy='day'">Day</button>
            <button type="button"
                    [style.font-weight]="resGroupBy==='month'?'700':'400'"
                    [style.color]="resGroupBy==='month'?'#2563eb':'#6b7280'"
                    [style.border-bottom]="resGroupBy==='month'?'2px solid #2563eb':'2px solid transparent'"
                    style="background:none;border:none;padding:2px 8px;cursor:pointer;font-size:13px;line-height:1.6"
                    (click)="resGroupBy='month'">Month</button>
            <button type="button"
                    [style.font-weight]="resGroupBy==='year'?'700':'400'"
                    [style.color]="resGroupBy==='year'?'#2563eb':'#6b7280'"
                    [style.border-bottom]="resGroupBy==='year'?'2px solid #2563eb':'2px solid transparent'"
                    style="background:none;border:none;padding:2px 8px;cursor:pointer;font-size:13px;line-height:1.6"
                    (click)="resGroupBy='year'">Year</button>
          </div>
        </div>

        <!-- ── Detail rows (no grouping) ─────────────────────────────────── -->
        @if (resGroupBy === 'none') {
          <p-table #ptResDet [value]="filteredResDetails" [paginator]="true" [rows]="25" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
            [scrollable]="true" scrollHeight="500px"
            styleClass="p-datatable-striped p-datatable-sm details-table"
            sortMode="single">
            <ng-template pTemplate="header">
              <!-- Column order: Order# | Property | Location | Development |
                   Revenue | Avg/Night | Check-In | Check-Out | Nights |
                   Guest | Email | Reservationist | Sale Date | Status | Source -->
              <tr>
                <th class="num" pSortableColumn="orderId"          style="width:82px;min-width:82px">Order # <p-sortIcon field="orderId"></p-sortIcon></th>
                <th pSortableColumn="propertyCode"                  style="width:95px;min-width:95px">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
                <th pSortableColumn="location"                      style="width:78px;min-width:78px">Location <p-sortIcon field="location"></p-sortIcon></th>
                <th pSortableColumn="development"                   style="width:140px;min-width:140px">Development <p-sortIcon field="development"></p-sortIcon></th>
                <th class="num" pSortableColumn="revenue"           style="width:95px;min-width:95px">Revenue
                  <i class="pi pi-info-circle info-icon" pTooltip="Owner Amt + KBM Fee (ex-tax) for this reservation." tooltipPosition="top"></i>
                  <p-sortIcon field="revenue"></p-sortIcon></th>
                <th class="num" pSortableColumn="avgNight"          style="width:88px;min-width:88px">Avg/Night
                  <i class="pi pi-info-circle info-icon" pTooltip="Revenue ÷ Nights. Average nightly rate earned on this reservation." tooltipPosition="top"></i>
                  <p-sortIcon field="avgNight"></p-sortIcon></th>
                <th pSortableColumn="checkIn"                       style="width:78px;min-width:78px">Check-In <p-sortIcon field="checkIn"></p-sortIcon></th>
                <th pSortableColumn="checkOut"                      style="width:78px;min-width:78px">Check-Out <p-sortIcon field="checkOut"></p-sortIcon></th>
                <th class="col-nights" pSortableColumn="numberOfNightsStay" style="width:58px;min-width:58px">Nights <p-sortIcon field="numberOfNightsStay"></p-sortIcon></th>
                <th pSortableColumn="guestName"                     style="width:115px;min-width:115px">Guest <p-sortIcon field="guestName"></p-sortIcon></th>
                <th pSortableColumn="guestEmail"                    style="width:150px;min-width:150px">Email <p-sortIcon field="guestEmail"></p-sortIcon></th>
                <th pSortableColumn="reservationistName"            style="width:115px;min-width:115px">Reservationist <p-sortIcon field="reservationistName"></p-sortIcon></th>
                <th pSortableColumn="saleDate"                      style="width:80px;min-width:80px">Sale Date <p-sortIcon field="saleDate"></p-sortIcon></th>
                <th pSortableColumn="statusLabel"                   style="width:95px;min-width:95px">Status <p-sortIcon field="statusLabel"></p-sortIcon></th>
                <th pSortableColumn="sourceLabel"                   style="width:82px;min-width:82px">Source <p-sortIcon field="sourceLabel"></p-sortIcon></th>
              </tr>
              <tr class="filter-row">
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'orderId',            'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'propertyCode',       'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'location',           'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'development',        'contains')"></th>
                <th></th>
                <th></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'checkIn',            'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'checkOut',           'contains')"></th>
                <th></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'guestName',          'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'guestEmail',         'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'reservationistName', 'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'saleDate',           'contains')"></th>
                <th><input pInputText type="text" placeholder="e.g. Confirmed" (input)="ptResDet.filter($any($event.target).value, 'statusLabel', 'contains')"></th>
                <th><input pInputText type="text" placeholder="Filter…" (input)="ptResDet.filter($any($event.target).value, 'sourceLabel',        'contains')"></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="num">
                  @if (row.orderId) {
                    <a href="https://admin.kbmhawaii.com/order/{{row.orderId}}"
                       target="_blank" rel="noopener noreferrer">{{ row.orderId }}</a>
                  } @else { — }
                </td>
                <td>{{ row.propertyCode || '—' }}</td>
                <td>{{ row.location || '—' }}</td>
                <td>{{ row.development || '—' }}</td>
                <td class="num">{{ formatMoney(row.revenue) }}</td>
                <td class="num">{{ row.avgNight > 0 ? formatMoney(row.avgNight) : '—' }}</td>
                <td>{{ row.checkIn  | date:'MM/dd/yy' }}</td>
                <td>{{ row.checkOut | date:'MM/dd/yy' }}</td>
                <td class="col-nights">{{ row.numberOfNightsStay ?? '—' }}</td>
                <td>{{ row.guestName || '—' }}</td>
                <td class="truncate">{{ row.guestEmail || '—' }}</td>
                <td>{{ row.reservationistName || '—' }}</td>
                <td>{{ row.saleDate | date:'MM/dd/yy' }}</td>
                <td>
                  <span class="status-pill"
                        [class.res-op]="row.reservationStatus === 'OP'"
                        [class.res-og]="row.reservationStatus === 'OG'"
                        [title]="row.reservationStatus">
                    {{ row.statusLabel }}
                  </span>
                </td>
                <td>{{ row.sourceLabel || '—' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr>
                <td colspan="4" style="font-style:italic;color:#6b7280">Total ({{ filteredResDetails.length }} rows)</td>
                <td class="num">{{ formatMoney(sumBy(filteredResDetails, 'revenue')) }}</td>
                <td class="num">
                  {{ sumBy(filteredResDetails, 'numberOfNightsStay') > 0
                      ? formatMoney(sumBy(filteredResDetails, 'revenue') / sumBy(filteredResDetails, 'numberOfNightsStay'))
                      : '—' }}
                </td>
                <td colspan="2"></td>
                <td class="col-nights num">{{ formatNumber(sumBy(filteredResDetails, 'numberOfNightsStay')) }}</td>
                <td colspan="6"></td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="15" class="text-center" style="padding:24px">
                @if (activeResFilter) {
                  No reservations match the "{{ activeResFilter }}" source filter.
                } @else {
                  Click a Reservation Revenue KPI tile above to drill into a source, or no reservations in this window.
                }
              </td></tr>
            </ng-template>
          </p-table>
        }

        <!-- ── Grouped view (day / month / year) ─────────────────────────── -->
        @if (resGroupBy !== 'none') {
          <p-table [value]="groupedResDetails" [paginator]="true" [rows]="25" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
            styleClass="p-datatable-striped p-datatable-sm"
            sortMode="single">
            <ng-template pTemplate="header">
              <tr>
                <th pSortableColumn="period">Period <p-sortIcon field="period"></p-sortIcon></th>
                <th class="num" pSortableColumn="count">Reservations <p-sortIcon field="count"></p-sortIcon></th>
                <th class="num" pSortableColumn="totalNights">Total Nights <p-sortIcon field="totalNights"></p-sortIcon></th>
                <th class="num" pSortableColumn="totalRevenue">Total Revenue <p-sortIcon field="totalRevenue"></p-sortIcon></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.period }}</td>
                <td class="num">{{ formatNumber(row.count) }}</td>
                <td class="num">{{ formatNumber(row.totalNights) }}</td>
                <td class="num">{{ formatMoney(row.totalRevenue) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="footer">
              <tr>
                <td style="font-style:italic;color:#6b7280">Total</td>
                <td class="num">{{ formatNumber(sumBy(groupedResDetails, 'count')) }}</td>
                <td class="num">{{ formatNumber(sumBy(groupedResDetails, 'totalNights')) }}</td>
                <td class="num">{{ formatMoney(sumBy(groupedResDetails, 'totalRevenue')) }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr><td colspan="4" class="text-center">No reservation data to group.</td></tr>
            </ng-template>
          </p-table>
        }
      </div>
    }
    }

    <!-- ════ SECTION 1 — QUOTE VOLUME & CONVERSION ════ -->
    <div class="section-header quote-section-header">
      <h4>① Quote — Volume &amp; Conversion</h4>
      <p class="section-desc">Are quotes going out and turning into bookings? Covers every quote sent in the window — how many guests were quoted, how many options booked, conversion rate, and the revenue captured vs. pipeline value.</p>
    </div>

    <!-- ════ KPI TILES ════ -->
    <div class="kpi-grid">
      <!-- Guests quoted / lines -->
      <div class="kpi-tile">
        <div class="kpi-label">Guests Quoted
          <i class="pi pi-info-circle info-icon"
             pTooltip="Unique guests who received at least one quote in this date range. If the same guest was sent multiple quote envelopes, they still count as 1. Tip: check the detail table to see all envelopes per guest."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link" (click)="drillDown('quote', '__SENT__', 'Quotes Sent')">{{ formatNumber(data.kpis.quotesSent) }}</div>
        <div class="kpi-sub kpi-link" (click)="drillDown('quote', '__SENT__', 'Quotes Sent')" style="cursor:pointer">{{ formatNumber(data.kpis.linesSent) }} options · avg {{ data.kpis.avgOptionsPerQuote | number:'1.1-1' }}/guest</div>
        <div class="kpi-delta" [class.positive]="delta('quotesSent').positive">{{ delta('quotesSent').text }}</div>
      </div>

      <!-- Booked -->
      <div class="kpi-tile booked">
        <div class="kpi-label">Booked
          <i class="pi pi-info-circle info-icon"
             pTooltip="Total booked option lines — every property option a guest confirms counts individually. If the same guest books 5 options, that counts as 5. Sub-line shows how many unique guests those bookings came from, and the total active option pool those bookings came out of."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link" (click)="drillDown('quote', 'Booked', 'Booked')">{{ formatNumber(data.kpis.linesBooked) }}</div>
        <div class="kpi-sub">
          {{ formatNumber(data.kpis.guestsBooked) }} unique guests
          @if (data.kpis.linesActivePool > 0) {
            · out of {{ formatNumber(data.kpis.linesActivePool) }} active options
          }
        </div>
        <div class="kpi-delta" [class.positive]="delta('linesBooked').positive">{{ delta('linesBooked').text }}</div>
      </div>

      <!-- Conversion rate -->
      <div class="kpi-tile">
        <div class="kpi-label">Conversion
          <i class="pi pi-info-circle info-icon"
             pTooltip="Guests who booked ÷ Guests quoted (GMP level). If a guest received 10 options and booked any 1 of them, that counts as 1 booked guest — not 10% option conversion. Same guest quoted across multiple quotes still counts as 1. This gives a true read on how many guests you converted."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatPct(data.kpis.conversionRate) }}</div>
        <div class="kpi-sub">guests booked / guests quoted</div>
        <div class="kpi-delta" [class.positive]="delta('conversionRate').positive">{{ delta('conversionRate').text }}</div>
      </div>

      <!-- Pending -->
      <div class="kpi-tile pending">
        <div class="kpi-label">Pending
          <i class="pi pi-info-circle info-icon"
             pTooltip="Options still in 'Quote Sent' status (QS). Guest hasn't booked yet, the quote hasn't expired, and nobody has cancelled it. These are live opportunities."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link" (click)="drillDown('quote', 'Sent', 'Pending')">{{ formatNumber(data.kpis.linesPending) }}</div>
        <div class="kpi-sub">still open (QS)</div>
      </div>

      <!-- Expired -->
      <div class="kpi-tile expired">
        <div class="kpi-label">Expired
          <i class="pi pi-info-circle info-icon"
             pTooltip="Options auto-flipped to QE by the daily expiry job (runs 5:30 AM) when the quote's expiration date passed without booking. Lower is better — delta arrow respects that."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link" (click)="drillDown('quote', 'Expired', 'Expired')">{{ formatNumber(data.kpis.linesExpired) }}</div>
        <div class="kpi-sub">flipped by expiry job</div>
        <div class="kpi-delta" [class.positive]="delta('linesExpired', true).positive">{{ delta('linesExpired', true).text }}</div>
      </div>

      <!-- Cancelled -->
      <div class="kpi-tile">
        <div class="kpi-label">Cancelled
          <i class="pi pi-info-circle info-icon"
             pTooltip="Options manually cancelled by the team (status QX). Usually because the guest declined or the property became unavailable before booking."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value kpi-link" (click)="drillDown('quote', 'Cancelled', 'Cancelled')">{{ formatNumber(data.kpis.linesCancelled) }}</div>
        <div class="kpi-sub">manually cancelled (QX)</div>
      </div>

      <!-- Booked revenue -->
      <div class="kpi-tile booked">
        <div class="kpi-label">Booked Value
          <i class="pi pi-info-circle info-icon"
             pTooltip="Total stay value of bookings closed in the Sale Date window (quotePrice × numNights). Real revenue captured. 'Quoted' on the sub-line is an expected-pipeline figure across the Quote-Create window: each guest is averaged across all their offered options (a guest only books one option, not all of them), then summed across guests."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.kpis.bookedValue) }}</div>
        <div class="kpi-sub">Quoted: {{ formatMoney(data.kpis.quotedValue) }}</div>
        <div class="kpi-delta" [class.positive]="delta('bookedValue').positive">{{ delta('bookedValue').text }}</div>
      </div>

      <!-- Avg quote value -->
      <div class="kpi-tile">
        <div class="kpi-label">Avg Quote $
          <i class="pi pi-info-circle info-icon"
             pTooltip="Average stay value per option we sent (quotePrice × numNights, averaged across options). Avg discount = average quoteDiscPct applied to options."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatMoney(data.kpis.avgQuoteValue) }}</div>
        <div class="kpi-sub">avg discount {{ formatPct(data.kpis.avgDiscountPct) }}</div>
      </div>
    </div>

    <!-- ════ SECTION 2 — QUOTE EMAIL ENGAGEMENT ════ -->
    <div class="section-header quote-section-header">
      <h4>② Quote — Email Engagement</h4>
      <p class="section-desc">Quote emails only. Did the quote email reach the guest and did they read it? Tracks delivery, opens, bounces, guest replies, team follow-ups, and two warning signals: guests who replied but never booked, and quotes where the email was never opened.</p>
    </div>

    <!-- ════ ENGAGEMENT KPI ROW ════ -->
    <div class="kpi-grid kpi-grid-secondary">
      <div class="kpi-tile small">
        <div class="kpi-label">Emails Delivered
          <i class="pi pi-info-circle info-icon"
             pTooltip="Recipients where SendGrid reported a 'delivered' event — i.e., the email reached the recipient's inbox provider successfully."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.emailsDelivered) }}</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Total Opens
          <i class="pi pi-info-circle info-icon"
             pTooltip="Cumulative open count across all quote emails. If one recipient opens the same email three times, that counts as 3."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.emailsOpened) }}</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Bounced
          <i class="pi pi-info-circle info-icon"
             pTooltip="Recipients where SendGrid reported a bounce. Usually means the email address is invalid or the inbox is full — worth scrubbing the address."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.emailsBounced) }}</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Guest Replies
          <i class="pi pi-info-circle info-icon"
             pTooltip="Inbound messages received on quote threads (status=RECEIVED). Sub-line shows how many distinct quotes had at least one reply — your engagement universe."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.guestReplies) }}</div>
        <div class="kpi-sub">on {{ formatNumber(data.kpis.quotesWithGuestReply) }} quotes</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Team Follow-ups
          <i class="pi pi-info-circle info-icon"
             pTooltip="Manual outbound messages your team sent on quote threads AFTER the original quote send. Measures team hand-holding effort."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.teamFollowUps) }}</div>
        <div class="kpi-sub">manual outbound touches</div>
      </div>
      <div class="kpi-tile small warn">
        <div class="kpi-label">Replied · Not Booked
          <i class="pi pi-info-circle info-icon"
             pTooltip="Quotes where the guest replied but never booked. These are high-intent leads that stalled — worth a personal re-engagement attempt."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.repliedNotBooked) }}</div>
        <div class="kpi-sub">high-intent re-engage list</div>
      </div>
      <div class="kpi-tile small warn">
        <div class="kpi-label">Zero Email Opens
          <i class="pi pi-info-circle info-icon"
             pTooltip="Quotes whose recipient never opened the email at all. Could mean spam folder, wrong address, or just low interest. Investigate if this number is large."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.quotesWithZeroOpens) }}</div>
        <div class="kpi-sub">deliverability risk</div>
      </div>
    </div>

    <!-- ════ SECTION 3 — QUOTE TIME & ACTION ════ -->
    <div class="section-header quote-section-header">
      <h4>③ Quote — Time &amp; Action</h4>
      <p class="section-desc">Quote pipeline speed and urgency. How fast guests reply, how long it takes to close a booking, average lead time to check-in, and a forward-looking list of quotes expiring in the next 24–72 hours that need a team nudge now.</p>
    </div>

    <!-- ════ TIME / EXPIRY KPI ROW ════ -->
    <div class="kpi-grid kpi-grid-tertiary">
      <div class="kpi-tile small accent">
        <div class="kpi-label">Avg Time to Book
          <i class="pi pi-info-circle info-icon"
             pTooltip="For bookings closed in the Sale Date window: average elapsed time from quote send to reservation sale date. Sales-cycle length — tells you when to push harder."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatHours(data.kpis.avgHoursToBook) }}</div>
      </div>
      <div class="kpi-tile small accent">
        <div class="kpi-label">Avg Lead Time
          <i class="pi pi-info-circle info-icon"
             pTooltip="Average days between quote creation and the check-in date. Your typical booking window — useful for staffing and inventory planning."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatDays(data.kpis.avgLeadTimeDays) }}</div>
        <div class="kpi-sub">sent → check-in</div>
      </div>
      <div class="kpi-tile small accent">
        <div class="kpi-label">Quotes Resent
          <i class="pi pi-info-circle info-icon"
             pTooltip="Quotes that were edited and saved again after creation (lastUpdate more than 5 minutes after createDate). A high number means first-shot quotes often need revision."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.quotesResent) }}</div>
        <div class="kpi-sub">edited &amp; resent</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Single-window
          <i class="pi pi-info-circle info-icon"
             pTooltip="Quote envelopes (not guests) that contained exactly 1 property option. Note: Single + Multi = total envelopes sent, which can exceed Guests Quoted because one guest may receive multiple separate envelopes (e.g. a revised quote sent as a new envelope)."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.quotesSingleWindow) }}</div>
        <div class="kpi-sub">envelopes</div>
      </div>
      <div class="kpi-tile small">
        <div class="kpi-label">Multi-window
          <i class="pi pi-info-circle info-icon"
             pTooltip="Quote envelopes that contained 2 or more property options. Single + Multi = total envelopes sent. A guest who received 3 separate envelopes (e.g. after revisions) counts as 1 guest but 3 envelopes."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.quotesMultiWindow) }}</div>
        <div class="kpi-sub">{{ formatNumber(data.kpis.quotesSingleWindow + data.kpis.quotesMultiWindow) }} total envelopes</div>
      </div>
      <div class="kpi-tile small action">
        <div class="kpi-label">Expiring · Next 24h
          <i class="pi pi-info-circle info-icon"
             pTooltip="Pending (QS) quote options that will expire within the next 24 hours. ACTION LIST — guests who need a nudge right now to avoid losing the booking."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.expiringNext24h) }}</div>
      </div>
      <div class="kpi-tile small action">
        <div class="kpi-label">Expiring · Next 72h
          <i class="pi pi-info-circle info-icon"
             pTooltip="Pending quote options expiring within the next 72 hours (3 days). Plan a follow-up sweep. Sub-line shows the tighter 48-hour bucket."
             tooltipPosition="top"></i>
        </div>
        <div class="kpi-value">{{ formatNumber(data.kpis.expiringNext72h) }}</div>
        <div class="kpi-sub">48h: {{ formatNumber(data.kpis.expiringNext48h) }}</div>
      </div>
    </div>

    <!-- ════ SECTION 4 — HOLD PIPELINE SNAPSHOT ════ -->
    @if (data.holdKpis) {
      <div class="section-header hold-section-header">
        <h4>④ Hold Pipeline — Snapshot</h4>
        <p class="section-desc">Reservation Holds — created, confirmed, expired, cancelled. A Hold can come from a quote (best-fit / quote-list) or be placed directly (tape-chart / phone). Breakdowns and row-level drill-down are in section ⑧ below.</p>
      </div>

      <!-- Hold KPI row 1 — Volume & Conversion -->
      <div class="kpi-grid">
        <div class="kpi-tile">
          <div class="kpi-label">Holds Created
            <i class="pi pi-info-circle info-icon"
               pTooltip="Reservations with holdDate in the window. Includes direct holds (no quote) AND holds created via the quote flow. Note: clicking this opens the full hold activity table, which may show more rows than this count — it also includes holds from outside the window that had a confirmation, expiration, or cancellation event inside the window."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('hold', '__HOLD_CREATED__', 'Holds Created')">{{ formatNumber(data.holdKpis.holdsCreated) }}</div>
        </div>
        <div class="kpi-tile booked">
          <div class="kpi-label">Confirmed
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds that transitioned to status OP (Sale Pending). If you picked a Sale Date filter: counts confirmations in that window (by saleDate). Otherwise: counts holds created in window that have since confirmed."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('hold', 'Confirmed', 'Confirmed')">{{ formatNumber(data.holdKpis.holdsConfirmed) }}</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-label">Conversion
            <i class="pi pi-info-circle info-icon"
               pTooltip="Confirmed ÷ Created. Higher = team is closing the holds they create."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatPct(data.holdKpis.conversionRate) }}</div>
        </div>
        <div class="kpi-tile pending">
          <div class="kpi-label">Pending
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds created in window that are still QH (active). Snapshot — work-in-progress pile."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('hold', 'Hold', 'Pending')">{{ formatNumber(data.holdKpis.holdsPending) }}</div>
        </div>
        <div class="kpi-tile expired">
          <div class="kpi-label">Expired
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds that flipped to QE without confirming. If you picked a Sale Date filter: counts expirations in window (by expiry date). Otherwise: counts holds created in window that have since expired."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('hold', 'Expired', 'Expired')">{{ formatNumber(data.holdKpis.holdsExpired) }}</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-label">Cancelled
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds that ultimately became OX (Sale Cancelled). Includes holds that confirmed and then cancelled."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('hold', 'Cancelled', 'Cancelled')">{{ formatNumber(data.holdKpis.holdsCancelled) }}</div>
        </div>
        <div class="kpi-tile booked">
          <div class="kpi-label">Confirmed Value
            <i class="pi pi-info-circle info-icon"
               pTooltip="Total nightly rental revenue of confirmed holds, sourced directly from the reservation ledger. Includes both direct holds and quote-linked holds."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatMoney(data.holdKpis.confirmedValue) }}</div>
          <div class="kpi-sub">Held: {{ formatMoney(data.holdKpis.holdValue) }}</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-label">Postponed
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds parked at PP (Sale Postponed). Usually pending guest decision."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.holdKpis.holdsPostponed) }}</div>
        </div>
      </div>

      <!-- Hold KPI row 2 — Time & Action -->
      <div class="kpi-grid kpi-grid-tertiary">
        <div class="kpi-tile small accent">
          <div class="kpi-label">Avg Hold Duration
            <i class="pi pi-info-circle info-icon"
               pTooltip="Avg time from holdDate → saleDate for confirmed holds. Lower = team is closing fast."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatHours(data.holdKpis.avgHoldDurationHours) }}</div>
        </div>
        <div class="kpi-tile small accent">
          <div class="kpi-label">Avg Lead Time
            <i class="pi pi-info-circle info-icon"
               pTooltip="Avg days from holdDate → check-in. Typical booking window for holds."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatDays(data.holdKpis.avgLeadTimeDays) }}</div>
          <div class="kpi-sub">hold → check-in</div>
        </div>
        <div class="kpi-tile small action">
          <div class="kpi-label">Expiring · Next 24h
            <i class="pi pi-info-circle info-icon"
               pTooltip="Active QH holds whose reservationHoldExpiration is in the next 24 hours. ACTION LIST — guest needs a nudge."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.holdKpis.expiringNext24h) }}</div>
        </div>
        <div class="kpi-tile small action">
          <div class="kpi-label">Expiring · Next 72h
            <i class="pi pi-info-circle info-icon"
               pTooltip="Active QH holds expiring within 72h. Sub-line shows the tighter 48h bucket."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.holdKpis.expiringNext72h) }}</div>
          <div class="kpi-sub">48h: {{ formatNumber(data.holdKpis.expiringNext48h) }}</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">From Quote
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds created via the quote flow (str_quote_detail.reservationId stamped). Measures how much hold volume the quote tool produces."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.holdKpis.holdsFromQuote) }}</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">Direct Holds
            <i class="pi pi-info-circle info-icon"
               pTooltip="Holds created without a quote — agent built them via tape chart or phone. Compare with 'From Quote' to see channel mix."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.holdKpis.holdsDirect) }}</div>
        </div>
      </div>
    }

    <!-- ════ SECTION 5 — UPSELL EXTENSION OFFERS SNAPSHOT ════ -->
    @if (data.upsellKpis) {
      <div class="section-header upsell-section-header">
        <h4>⑤ Upsell Extension Offers — Snapshot</h4>
        <p class="section-desc">Promotion offers sent to existing reservations to add one extra night (before check-in or after check-out). Tracks sent, accepted, applied, pending, and revenue captured. Drill-down is in section ⑨ below.</p>
      </div>

      <!-- KPI row 1 — Volume & Conversion -->
      <div class="kpi-grid">
        <div class="kpi-tile">
          <div class="kpi-label">Total Offers Sent
            <i class="pi pi-info-circle info-icon"
               pTooltip="Upsell offers with a lifecycle event in the window — offer sent, guest accepted, reservationist applied, or offer expired/declined. Click to see every offer in the drill-down table (section ⑨ below)."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('upsell', '__UPSELL_CREATED__', 'Offers Sent')">{{ formatNumber(data.upsellKpis.offersTotal) }}</div>
          <div class="kpi-sub">{{ formatPct(data.upsellKpis.conversionRate) }} conversion</div>
        </div>
        <div class="kpi-tile booked">
          <div class="kpi-label">Applied
            <i class="pi pi-info-circle info-icon"
               pTooltip="Offers where the reservationist extended the reservation. Revenue was captured."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('upsell', 'APPLIED', 'Applied')">{{ formatNumber(data.upsellKpis.offersApplied) }}</div>
          <div class="kpi-sub">extensions applied</div>
        </div>
        <div class="kpi-tile pending">
          <div class="kpi-label">Pending
            <i class="pi pi-info-circle info-icon"
               pTooltip="Still actionable: OFFERED (awaiting guest), ACCEPTED (guest said yes, team to apply), or PARTIALLY_APPLIED (one side of a dual-direction offer done, one still open)."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('upsell', 'PENDING', 'Pending')">{{ formatNumber(data.upsellKpis.offersPending) }}</div>
          <div class="kpi-sub">OFFERED + ACCEPTED + partial</div>
        </div>
        <div class="kpi-tile expired">
          <div class="kpi-label">Expired
            <i class="pi pi-info-circle info-icon"
               pTooltip="Auto-expired after 72h without action — missed revenue. Lower is better."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('upsell', 'EXPIRED', 'Expired')">{{ formatNumber(data.upsellKpis.offersExpired) }}</div>
          <div class="kpi-sub">timed out</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-label">Declined
            <i class="pi pi-info-circle info-icon"
               pTooltip="Explicitly declined by guest or cancelled by team."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value kpi-link" (click)="drillDown('upsell', 'DECLINED', 'Declined')">{{ formatNumber(data.upsellKpis.offersDeclined) }}</div>
          <div class="kpi-sub">declined</div>
        </div>
        <div class="kpi-tile">
          <div class="kpi-label">Conversion
            <i class="pi pi-info-circle info-icon"
               pTooltip="Applied ÷ Total sent. Share of offers that turned into revenue."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatPct(data.upsellKpis.conversionRate) }}</div>
          <div class="kpi-sub">applied / sent</div>
        </div>
      </div>

      <!-- KPI row 2 — Revenue -->
      <div class="kpi-grid kpi-grid-secondary">
        <div class="kpi-tile small booked">
          <div class="kpi-label">Revenue Applied
            <i class="pi pi-info-circle info-icon"
               pTooltip="Total extra-night revenue captured (CC charged + manual combined) across all APPLIED offers."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatMoney(data.upsellKpis.revenueApplied) }}</div>
          <div class="kpi-sub">CC + manual</div>
        </div>
        <div class="kpi-tile small booked">
          <div class="kpi-label">Revenue Charged (CC)
            <i class="pi pi-info-circle info-icon"
               pTooltip="Sum of successful CC auto-charges — actual cash collected."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatMoney(data.upsellKpis.revenueCharged) }}</div>
          <div class="kpi-sub">CC collected</div>
        </div>
        <div class="kpi-tile small accent">
          <div class="kpi-label">Avg Extra-Night $
            <i class="pi pi-info-circle info-icon"
               pTooltip="Revenue Applied ÷ Applied count. Average incremental revenue per closed offer."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatMoney(data.upsellKpis.avgExtraNightValue) }}</div>
          <div class="kpi-sub">per applied offer</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">Avg Discount %
            <i class="pi pi-info-circle info-icon"
               pTooltip="Average effective discount % across all offers in the window."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatPct(data.upsellKpis.avgDiscountPct, true) }}</div>
          <div class="kpi-sub">avg effective discount</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">CC Approved
            <i class="pi pi-info-circle info-icon"
               pTooltip="Applied offers where the automatic CC charge succeeded."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.upsellKpis.chargesApproved) }}</div>
          <div class="kpi-sub">auto-charged OK</div>
        </div>
        <div class="kpi-tile small warn">
          <div class="kpi-label">CC Failed
            <i class="pi pi-info-circle info-icon"
               pTooltip="Charge attempted but declined — needs manual follow-up."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.upsellKpis.chargesFailed) }}</div>
          <div class="kpi-sub">charge declined</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">Manual Collect
            <i class="pi pi-info-circle info-icon"
               pTooltip="Applied without a CC charge attempt — team will collect offline."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.upsellKpis.chargesManual) }}</div>
          <div class="kpi-sub">no CC attempt</div>
        </div>
        <div class="kpi-tile small">
          <div class="kpi-label">Direction Mix
            <i class="pi pi-info-circle info-icon"
               pTooltip="NEXT = extend check-out by 1 night. PREV = extend check-in back 1 night. The main number is NEXT + PREV combined. Dual-direction offers count in both, so the total can exceed Offers Sent."
               tooltipPosition="top"></i>
          </div>
          <div class="kpi-value">{{ formatNumber(data.upsellKpis.offersNext + data.upsellKpis.offersPrev) }}</div>
          <div class="kpi-sub">{{ formatNumber(data.upsellKpis.offersNext) }} NEXT · {{ formatNumber(data.upsellKpis.offersPrev) }} PREV</div>
        </div>
      </div>
    }

    <!-- ════ SECTION 6 — QUOTE TRENDS & BREAKDOWNS ════ -->
    <div class="section-header quote-section-header">
      <h4>⑥ Quote — Trends &amp; Breakdowns</h4>
      <p class="section-desc">Quote data only. Daily sent vs. booked trend, status mix, and breakdowns by reservationist, bedroom count, and property. Use these to find patterns the headline KPIs above hide.</p>
    </div>

    <!-- ════ TREND CHART + STATUS MIX ROW ════ -->
    <div class="two-col-row">
      <div class="card">
        <div class="card-header">
          <h3>Daily Volume — Sent vs Booked vs Expired
            <i class="pi pi-info-circle info-icon"
               pTooltip="Stacked daily counts of option-level activity. Blue = options sent that day, green = options that ended up booked, red = options that expired. Look for days where sent spikes but booked doesn't — signals discount/inventory problems."
               tooltipPosition="top"></i>
          </h3>
        </div>
        <div class="chart-wrap chart-wrap-wide">
          <p-chart type="bar" [data]="dailyChartData" [options]="dailyChartOptions" height="280px"></p-chart>
        </div>
      </div>
      <div class="card">
        <div class="card-header"><h3>Status Mix
          <i class="pi pi-info-circle info-icon"
             pTooltip="Distribution of all quote options active in this window — sent (QS), booked (QC), expired (QE), cancelled (QX). In Activity Mode includes all lines that had any event (sent, booked, or expired/cancelled) in the window."
             tooltipPosition="top"></i>
        </h3></div>
        <div class="chart-wrap" style="max-width:360px;margin:0 auto">
          <p-chart type="doughnut" [data]="statusChartData" [options]="statusChartOptions" height="280px"></p-chart>
        </div>
      </div>
    </div>


    <!-- ════ RESERVATIONIST BREAKDOWN ════ -->
    <div class="card">
      <div class="card-header"><h3>By Reservationist
        <i class="pi pi-info-circle info-icon"
           pTooltip="Per-agent performance. Compare 'Conv %' to spot training opportunities, and 'Booked $' for revenue contribution. Quotes Sent vs Options shows whether an agent typically offers multi-property quotes."
           tooltipPosition="top"></i>
      </h3></div>
      <p-table #ptRes [value]="data.byReservationist || []" styleClass="p-datatable-striped p-datatable-sm"
        sortMode="single">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="reservationistName">Reservationist <p-sortIcon field="reservationistName"></p-sortIcon></th>
            <th class="num" pSortableColumn="quotesSent">Guests Quoted <p-sortIcon field="quotesSent"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesSent">Options <p-sortIcon field="linesSent"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesBooked">Booked <p-sortIcon field="linesBooked"></p-sortIcon></th>
            <th class="num" pSortableColumn="conversionRate">Conv % <p-sortIcon field="conversionRate"></p-sortIcon></th>
            <th class="num" pSortableColumn="bookedValue">Booked $ <p-sortIcon field="bookedValue"></p-sortIcon></th>
          </tr>
          <tr class="filter-row">
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'reservationistName', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'quotesSent', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'linesSent', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'linesBooked', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'conversionRate', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptRes.filter($any($event.target).value, 'bookedValue', 'contains')"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.reservationistName }}</td>
            <td class="num">{{ formatNumber(row.quotesSent) }}</td>
            <td class="num">{{ formatNumber(row.linesSent) }}</td>
            <td class="num">{{ formatNumber(row.linesBooked) }}</td>
            <td class="num">{{ formatPct(row.conversionRate) }}</td>
            <td class="num">{{ formatMoney(row.bookedValue) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="footer">
          <tr>
            <td style="font-style:italic;color:#6b7280">Total</td>
            <td class="num">{{ formatNumber(sumBy(data.byReservationist, 'quotesSent')) }}</td>
            <td class="num">{{ formatNumber(sumBy(data.byReservationist, 'linesSent')) }}</td>
            <td class="num">{{ formatNumber(sumBy(data.byReservationist, 'linesBooked')) }}</td>
            <td></td>
            <td class="num">{{ formatMoney(sumBy(data.byReservationist, 'bookedValue')) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="text-center">No quotes in window.</td></tr></ng-template>
      </p-table>
    </div>

    <!-- ════ BEDROOM COUNT ════ -->
    <div class="card">
      <div class="card-header"><h3>By Bedroom Count
        <i class="pi pi-info-circle info-icon"
           pTooltip="Inventory insight: which property sizes are most quoted vs most booked? If 4BR options are quoted heavily but rarely book, you may have a pricing or supply problem at that size."
           tooltipPosition="top"></i>
      </h3></div>
      <p-table #ptBed [value]="data.byBedroom || []" styleClass="p-datatable-striped p-datatable-sm" sortMode="single">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="bedrooms">Bedrooms <p-sortIcon field="bedrooms"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesSent">Sent <p-sortIcon field="linesSent"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesBooked">Booked <p-sortIcon field="linesBooked"></p-sortIcon></th>
            <th class="num" pSortableColumn="conversionRate">Conv % <p-sortIcon field="conversionRate"></p-sortIcon></th>
          </tr>
          <tr class="filter-row">
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptBed.filter($any($event.target).value, 'bedrooms', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptBed.filter($any($event.target).value, 'linesSent', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptBed.filter($any($event.target).value, 'linesBooked', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptBed.filter($any($event.target).value, 'conversionRate', 'contains')"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.bedrooms ?? '—' }} BR</td>
            <td class="num">{{ formatNumber(row.linesSent) }}</td>
            <td class="num">{{ formatNumber(row.linesBooked) }}</td>
            <td class="num">{{ formatPct(row.conversionRate) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="footer">
          <tr>
            <td style="font-style:italic;color:#6b7280">Total</td>
            <td class="num">{{ formatNumber(sumBy(data.byBedroom, 'linesSent')) }}</td>
            <td class="num">{{ formatNumber(sumBy(data.byBedroom, 'linesBooked')) }}</td>
            <td></td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- ════ PROPERTY BREAKDOWN ════ -->
    <div class="card">
      <div class="card-header"><h3>By Property (top 100 by volume)
        <i class="pi pi-info-circle info-icon"
           pTooltip="Per-property quote performance. Sorted by options sent descending. Look for: properties quoted often but never booked (price/positioning issues), or unusually high discount % (margin leakage)."
           tooltipPosition="top"></i>
      </h3></div>
      <p-table #ptProp [value]="data.byProperty || []" styleClass="p-datatable-striped p-datatable-sm"
        [paginator]="true" [rows]="20" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
        [scrollable]="true" scrollHeight="450px"
        sortMode="single"
        [globalFilterFields]="['propertyCode']">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesSent">Options Sent <p-sortIcon field="linesSent"></p-sortIcon></th>
            <th class="num" pSortableColumn="linesBooked">Booked <p-sortIcon field="linesBooked"></p-sortIcon></th>
            <th class="num" pSortableColumn="conversionRate">Conv % <p-sortIcon field="conversionRate"></p-sortIcon></th>
            <th class="num" pSortableColumn="quotedValue">Quoted $ <p-sortIcon field="quotedValue"></p-sortIcon></th>
            <th class="num" pSortableColumn="bookedValue">Booked $ <p-sortIcon field="bookedValue"></p-sortIcon></th>
            <th class="num" pSortableColumn="avgDiscountPct">Avg Disc % <p-sortIcon field="avgDiscountPct"></p-sortIcon></th>
          </tr>
          <tr class="filter-row">
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'propertyCode', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'linesSent', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'linesBooked', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'conversionRate', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'quotedValue', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'bookedValue', 'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…" (input)="ptProp.filter($any($event.target).value, 'avgDiscountPct', 'contains')"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>{{ row.propertyCode || '—' }}</td>
            <td class="num">{{ formatNumber(row.linesSent) }}</td>
            <td class="num">{{ formatNumber(row.linesBooked) }}</td>
            <td class="num">{{ formatPct(row.conversionRate) }}</td>
            <td class="num">{{ formatMoney(row.quotedValue) }}</td>
            <td class="num">{{ formatMoney(row.bookedValue) }}</td>
            <td class="num">{{ formatPct(row.avgDiscountPct) }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="footer">
          <tr>
            <td style="font-style:italic;color:#6b7280">Total (top 100)</td>
            <td class="num">{{ formatNumber(sumBy(data.byProperty, 'linesSent')) }}</td>
            <td class="num">{{ formatNumber(sumBy(data.byProperty, 'linesBooked')) }}</td>
            <td></td>
            <td class="num">{{ formatMoney(sumBy(data.byProperty, 'quotedValue')) }}</td>
            <td class="num">{{ formatMoney(sumBy(data.byProperty, 'bookedValue')) }}</td>
            <td></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="text-center">No quotes in window.</td></tr></ng-template>
      </p-table>
    </div>

    <!-- ════ SECTION 7 — QUOTE DRILL-DOWN ════ -->
    <div class="section-header quote-section-header">
      <h4>⑦ Quote — Drill-Down</h4>
      <p class="section-desc">Quote data only. One row per quote option — the raw detail behind every number in sections ①–⑥. Click the Mask link to open the guest's quote view. Click Order # to open the booked reservation in admin.</p>
    </div>

    <!-- ════ DRILL-DOWN DETAIL ════ -->
    <div class="card" id="quote-detail-anchor">
      <div class="card-header">
        <h3>Quote Drill-Down ({{ filteredDetails.length }} of {{ data.details?.length ?? 0 }} rows)
          @if (activeQuoteFilter) {
            <span class="drill-badge">{{ activeQuoteFilter }} <button class="drill-clear" (click)="clearDrillDown('quote')" title="Clear filter">✕</button></span>
          }
          <i class="pi pi-info-circle info-icon"
             pTooltip="Row-level view of every option in the window. Click the Mask link to open the guest's view in a new tab. Click the Res ID link to open the booked reservation. Use the search box (or column sort) to drill into specific guests, properties, or statuses. Capped at 1000 rows to keep the page snappy."
             tooltipPosition="top"></i>
        </h3>
        <span class="card-header-note">Click the mask link to open the guest's view in a new tab.</span>
      </div>
      <p-table #ptDetail [value]="filteredDetails" [paginator]="true" [rows]="25" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
        [scrollable]="true" scrollHeight="500px"
        styleClass="p-datatable-striped p-datatable-sm details-table"
        sortMode="single"
        [globalFilterFields]="['quoteIdMask','propertyCode','guestLastName','guestEmail','reservationistName','quoteStatusLabel']">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="quoteIdMask">Mask
              <i class="pi pi-info-circle info-icon" pTooltip="Quote hash link — click to open the guest's itinerary page." tooltipPosition="top"></i>
              <p-sortIcon field="quoteIdMask"></p-sortIcon>
            </th>
            <th pSortableColumn="createDate">Created
              <i class="pi pi-info-circle info-icon" pTooltip="Date &amp; time the quote was sent to the guest (sq.createDate, NY time)." tooltipPosition="top"></i>
              <p-sortIcon field="createDate"></p-sortIcon>
            </th>
            <th pSortableColumn="quoteExpirationDateTime">Expires
              <i class="pi pi-info-circle info-icon" pTooltip="Date the quote offer expires. After this date the guest can no longer book at the quoted price." tooltipPosition="top"></i>
              <p-sortIcon field="quoteExpirationDateTime"></p-sortIcon>
            </th>
            <th pSortableColumn="checkInDateTime">Check In
              <i class="pi pi-info-circle info-icon" pTooltip="Proposed check-in date for this option." tooltipPosition="top"></i>
              <p-sortIcon field="checkInDateTime"></p-sortIcon>
            </th>
            <th pSortableColumn="checkOutDateTime">Check Out
              <i class="pi pi-info-circle info-icon" pTooltip="Proposed check-out date for this option." tooltipPosition="top"></i>
              <p-sortIcon field="checkOutDateTime"></p-sortIcon>
            </th>
            <th class="col-nights" pSortableColumn="numNights">Nights
              <i class="pi pi-info-circle info-icon" pTooltip="Number of nights for this option (checkOut − checkIn)." tooltipPosition="top"></i>
              <p-sortIcon field="numNights"></p-sortIcon>
            </th>
            <th pSortableColumn="guestLastName">Guest <p-sortIcon field="guestLastName"></p-sortIcon></th>
            <th pSortableColumn="guestEmail">Email <p-sortIcon field="guestEmail"></p-sortIcon></th>
            <th pSortableColumn="reservationistName">Reservationist <p-sortIcon field="reservationistName"></p-sortIcon></th>
            <th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
            <th class="num" pSortableColumn="quotePrice">Price/Night
              <i class="pi pi-info-circle info-icon" pTooltip="Discounted nightly rate quoted to the guest (quotePrice). Any coupon or manual discount has already been applied." tooltipPosition="top"></i>
              <p-sortIcon field="quotePrice"></p-sortIcon>
            </th>
            <th class="num">Total
              <i class="pi pi-info-circle info-icon" pTooltip="Total quote value = Price/Night × Nights. This is what the guest would pay for the full stay at the quoted rate." tooltipPosition="top"></i>
            </th>
            <th pSortableColumn="quoteStatusLabel">Status
              <i class="pi pi-info-circle info-icon" pTooltip="QS = Sent (pending), QC = Booked, QE = Expired (by expiry job), QX = Cancelled (manual)." tooltipPosition="top"></i>
              <p-sortIcon field="quoteStatusLabel"></p-sortIcon>
            </th>
            <th pSortableColumn="saleDate">Sale Date
              <i class="pi pi-info-circle info-icon" pTooltip="Date the reservation was booked (r.saleDate). Blank for options that were not booked." tooltipPosition="top"></i>
              <p-sortIcon field="saleDate"></p-sortIcon>
            </th>
            <th class="num" pSortableColumn="openedCount">Opens
              <i class="pi pi-info-circle info-icon" pTooltip="Number of times the quote email was opened by the guest." tooltipPosition="top"></i>
              <p-sortIcon field="openedCount"></p-sortIcon>
            </th>
            <th class="num" pSortableColumn="clickedCount">Clicks
              <i class="pi pi-info-circle info-icon" pTooltip="Number of times the guest clicked a link inside the quote email." tooltipPosition="top"></i>
              <p-sortIcon field="clickedCount"></p-sortIcon>
            </th>
            <th class="num" pSortableColumn="replyCount">Replies
              <i class="pi pi-info-circle info-icon" pTooltip="Number of inbound guest replies on this quote thread." tooltipPosition="top"></i>
              <p-sortIcon field="replyCount"></p-sortIcon>
            </th>
            <th class="num" pSortableColumn="orderId">Order #
              <i class="pi pi-info-circle info-icon" pTooltip="Reservation order number. Click to open the booking in the admin tool. Blank for un-booked options." tooltipPosition="top"></i>
              <p-sortIcon field="orderId"></p-sortIcon>
            </th>
          </tr>
          <tr class="filter-row">
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'quoteIdMask',             'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'createDate',              'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'quoteExpirationDateTime', 'contains')"></th>
            <th><input pInputText type="text" placeholder="e.g. Jun 1" (input)="ptDetail.filter($any($event.target).value, 'checkInDateTime',         'contains')"></th>
            <th><input pInputText type="text" placeholder="e.g. Jun 4" (input)="ptDetail.filter($any($event.target).value, 'checkOutDateTime',        'contains')"></th>
            <th><input pInputText type="text" placeholder="e.g. 3"     (input)="ptDetail.filter($any($event.target).value, 'numNights',               'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'guestLastName',           'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'guestEmail',              'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'reservationistName',      'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'propertyCode',            'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'quotePrice',              'contains')"></th>
            <th></th>
            <th><input pInputText type="text" placeholder="e.g. Sent"  (input)="ptDetail.filter($any($event.target).value, 'quoteStatusLabel',        'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'saleDate',                'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'openedCount',             'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'clickedCount',            'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'replyCount',              'contains')"></th>
            <th><input pInputText type="text" placeholder="Filter…"    (input)="ptDetail.filter($any($event.target).value, 'orderId',                 'contains')"></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td>
              @if (row.quoteIdMask) {
                <a href="https://www.kbmresorts.com/guest-quote/{{row.quoteIdMask}}"
                   target="_blank" rel="noopener noreferrer">{{ row.quoteIdMask }}</a>
              } @else { — }
            </td>
            <td>{{ row.createDate | date:'MM/dd HH:mm':'America/New_York' }}</td>
            <td>{{ row.quoteExpirationDateTime | date:'MM/dd':'America/New_York' }}</td>
            <td>{{ row.checkInDateTime  | date:'MM/dd' }}</td>
            <td>{{ row.checkOutDateTime | date:'MM/dd' }}</td>
            <td class="col-nights">{{ row.numNights }}</td>
            <td>{{ row.guestFirstName }} {{ row.guestLastName }}</td>
            <td class="truncate">{{ row.guestEmail }}</td>
            <td>{{ row.reservationistName }}</td>
            <td>{{ row.propertyCode }}</td>
            <td class="num">{{ formatMoney(row.quotePrice) }}</td>
            <td class="num">{{ row.quotePrice && row.numNights ? formatMoney(row.quotePrice * row.numNights) : '—' }}</td>
            <td>
              <span class="status-pill" [class.qs]="row.quoteStatus==='QS'" [class.qc]="row.quoteStatus==='QC'"
                    [class.qe]="row.quoteStatus==='QE'" [class.qx]="row.quoteStatus==='QX'"
                    [title]="row.quoteStatus">{{ row.quoteStatusLabel }}</span>
            </td>
            <td>{{ row.saleDate ? (row.saleDate | date:'MM/dd':'America/New_York') : '—' }}</td>
            <td class="num">{{ row.openedCount ?? 0 }}</td>
            <td class="num">{{ row.clickedCount ?? 0 }}</td>
            <td class="num">{{ row.replyCount ?? 0 }}</td>
            <td class="num">
              @if (row.orderId) {
                <!-- Admin tool URL pattern: /order/{orderId}, not /reservation/{reservationId}.
                     The two IDs are distinct — str_reservation.orderId is a FK to str_order. -->
                <a href="https://admin.kbmhawaii.com/order/{{row.orderId}}"
                   target="_blank" rel="noopener noreferrer">{{ row.orderId }}</a>
              } @else { — }
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="footer">
          <tr>
            <td colspan="5" style="font-style:italic;color:#6b7280">Total ({{ filteredDetails.length }} rows)</td>
            <td class="num">{{ formatNumber(sumBy(filteredDetails, 'numNights')) }}</td>
            <td colspan="4"></td>
            <td></td>
            <td class="num">{{ formatMoney(sumQuoteTotal(filteredDetails)) }}</td>
            <td colspan="6"></td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage"><tr><td colspan="18" class="text-center">No quotes in window.</td></tr></ng-template>
      </p-table>
    </div>

    <!-- ════ HOLD PIPELINE ════════════════════════════════════════════════ -->
    <!-- ⑧ Breakdowns & Drill-Down (KPI Snapshot is section ④ above)          -->
    <!-- ════════════════════════════════════════════════════════════════════ -->
    @if (data.holdKpis) {
      <!-- ⑧ Hold Pipeline — Breakdowns & Drill-Down -->
      <div class="section-header hold-section-header">
        <h4>⑧ Hold Pipeline — Breakdowns &amp; Drill-Down</h4>
        <p class="section-desc">Hold data only. Per-reservationist breakdown and row-level drill-down. Headline Hold KPIs are in section ④ above.</p>
      </div>

      <!-- Hold By Reservationist -->
      <div class="card">
        <div class="card-header"><h3>Holds by Reservationist
          <i class="pi pi-info-circle info-icon"
             pTooltip="Per-agent hold performance. Compare Conv % to spot who closes holds best vs who has lots of expirations. Sortable + filterable."
             tooltipPosition="top"></i>
        </h3></div>
        <p-table #ptHRes [value]="data.holdByReservationist || []"
          styleClass="p-datatable-striped p-datatable-sm" sortMode="single">
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="reservationistName">Reservationist <p-sortIcon field="reservationistName"></p-sortIcon></th>
              <th class="num" pSortableColumn="holdsCreated">Created <p-sortIcon field="holdsCreated"></p-sortIcon></th>
              <th class="num" pSortableColumn="holdsConfirmed">Confirmed <p-sortIcon field="holdsConfirmed"></p-sortIcon></th>
              <th class="num" pSortableColumn="holdsExpired">Expired <p-sortIcon field="holdsExpired"></p-sortIcon></th>
              <th class="num" pSortableColumn="conversionRate">Conv % <p-sortIcon field="conversionRate"></p-sortIcon></th>
              <th class="num" pSortableColumn="confirmedValue">Confirmed $ <p-sortIcon field="confirmedValue"></p-sortIcon></th>
            </tr>
            <tr class="filter-row">
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'reservationistName', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'holdsCreated', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'holdsConfirmed', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'holdsExpired', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'conversionRate', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptHRes.filter($any($event.target).value, 'confirmedValue', 'contains')"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.reservationistName }}</td>
              <td class="num">{{ formatNumber(row.holdsCreated) }}</td>
              <td class="num">{{ formatNumber(row.holdsConfirmed) }}</td>
              <td class="num">{{ formatNumber(row.holdsExpired) }}</td>
              <td class="num">{{ formatPct(row.conversionRate) }}</td>
              <td class="num">{{ formatMoney(row.confirmedValue) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td style="font-style:italic;color:#6b7280">Total</td>
              <td class="num">{{ formatNumber(sumBy(data.holdByReservationist, 'holdsCreated')) }}</td>
              <td class="num">{{ formatNumber(sumBy(data.holdByReservationist, 'holdsConfirmed')) }}</td>
              <td class="num">{{ formatNumber(sumBy(data.holdByReservationist, 'holdsExpired')) }}</td>
              <td></td>
              <td class="num">{{ formatMoney(sumBy(data.holdByReservationist, 'confirmedValue')) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="text-center">No holds in window.</td></tr></ng-template>
        </p-table>
      </div>

      <!-- Hold Drill-Down -->
      <div class="card" id="hold-detail-anchor">
        <div class="card-header">
          <h3>Hold Drill-Down ({{ filteredHoldDetails.length }} of {{ data.holdDetails?.length ?? 0 }} rows)
            @if (activeHoldFilter) {
              <span class="drill-badge">{{ activeHoldFilter }} <button class="drill-clear" (click)="clearDrillDown('hold')" title="Clear filter">✕</button></span>
            }
            <i class="pi pi-info-circle info-icon"
               pTooltip="Row-level view of every hold that touched the window. Click Order # to open the admin order page. If the hold came from a quote, the Quote Mask column links to the guest's view."
               tooltipPosition="top"></i>
          </h3>
        </div>
        <p-table #ptHDet [value]="filteredHoldDetails" [paginator]="true" [rows]="25" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
          [scrollable]="true" scrollHeight="500px"
          styleClass="p-datatable-striped p-datatable-sm details-table"
          sortMode="single"
          [globalFilterFields]="['propertyCode','guestLastName','guestEmail','guestPhone','reservationistName','reservationStatusLabel']">
          <ng-template pTemplate="header">
            <tr>
              <th class="num" pSortableColumn="orderId">Order # <p-sortIcon field="orderId"></p-sortIcon></th>
              <th pSortableColumn="reservationStatusLabel">Status <p-sortIcon field="reservationStatusLabel"></p-sortIcon></th>
              <th pSortableColumn="holdDate">Hold Date <p-sortIcon field="holdDate"></p-sortIcon></th>
              <th pSortableColumn="reservationHoldExpiration">Expires <p-sortIcon field="reservationHoldExpiration"></p-sortIcon></th>
              <th pSortableColumn="saleDate">Sale Date <p-sortIcon field="saleDate"></p-sortIcon></th>
              <th pSortableColumn="checkInDateTime">Check-In <p-sortIcon field="checkInDateTime"></p-sortIcon></th>
              <th pSortableColumn="checkOutDateTime">Check-Out <p-sortIcon field="checkOutDateTime"></p-sortIcon></th>
              <th class="col-nights" pSortableColumn="numNights">Nights <p-sortIcon field="numNights"></p-sortIcon></th>
              <th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
              <th pSortableColumn="guestLastName">Guest <p-sortIcon field="guestLastName"></p-sortIcon></th>
              <th pSortableColumn="guestEmail">Email <p-sortIcon field="guestEmail"></p-sortIcon></th>
              <th pSortableColumn="guestPhone">Phone <p-sortIcon field="guestPhone"></p-sortIcon></th>
              <th pSortableColumn="reservationistName">Reservationist <p-sortIcon field="reservationistName"></p-sortIcon></th>
              <th class="num" pSortableColumn="stayValue">Value
                <i class="pi pi-info-circle info-icon"
                   pTooltip="Total nightly rental revenue from the reservation ledger (actual billed amount). Works for both direct holds and quote-linked holds."
                   tooltipPosition="top"></i>
                <p-sortIcon field="stayValue"></p-sortIcon>
              </th>
            </tr>
            <tr class="filter-row">
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'orderId',                  'contains')"></th>
              <th><input pInputText type="text" placeholder="e.g. Hold" (input)="ptHDet.filter($any($event.target).value, 'reservationStatusLabel',    'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'holdDate',                  'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'reservationHoldExpiration', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'saleDate',                  'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'checkInDateTime',           'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'checkOutDateTime',          'contains')"></th>
              <th></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'propertyCode',              'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'guestLastName',             'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'guestEmail',                'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'guestPhone',                'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'reservationistName',        'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…"   (input)="ptHDet.filter($any($event.target).value, 'stayValue',                 'contains')"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="num">
                @if (row.orderId) {
                  <a href="https://admin.kbmhawaii.com/order/{{row.orderId}}"
                     target="_blank" rel="noopener noreferrer">{{ row.orderId }}</a>
                } @else { — }
              </td>
              <td>
                <span class="status-pill"
                      [class.qs]="row.reservationStatus==='QH'"
                      [class.qc]="row.reservationStatus==='OP' || row.reservationStatus==='OC'"
                      [class.qe]="row.reservationStatus==='QE'"
                      [class.qx]="row.reservationStatus==='OX' || row.reservationStatus==='PP'"
                      [title]="row.reservationStatus">{{ row.reservationStatusLabel }}</span>
              </td>
              <td>{{ row.holdDate | date:'MM/dd':'America/New_York' }}</td>
              <td>{{ row.reservationHoldExpiration | date:'MM/dd HH:mm':'America/New_York' }}</td>
              <td>{{ row.saleDate | date:'MM/dd':'America/New_York' }}</td>
              <td>{{ row.checkInDateTime | date:'MM/dd' }}</td>
              <td>{{ row.checkOutDateTime | date:'MM/dd' }}</td>
              <td class="col-nights">{{ row.numNights }}</td>
              <td>{{ row.propertyCode }}</td>
              <td>{{ row.guestFirstName }} {{ row.guestLastName }}</td>
              <td class="truncate">{{ row.guestEmail }}</td>
              <td>{{ row.guestPhone }}</td>
              <td>{{ row.reservationistName }}</td>
              <td class="num">{{ formatMoney(row.stayValue) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td colspan="7" style="font-style:italic;color:#6b7280">Total ({{ filteredHoldDetails.length }} rows)</td>
              <td class="col-nights num">{{ formatNumber(sumBy(filteredHoldDetails, 'numNights')) }}</td>
              <td colspan="5"></td>
              <td class="num">{{ formatMoney(sumBy(filteredHoldDetails, 'stayValue')) }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="14" class="text-center">No holds in window.</td></tr></ng-template>
        </p-table>
      </div>
    }

    <!-- ════════════════════════════════════════════════════════════════════ -->
    <!-- ════ UPSELL PIPELINE ══════════════════════════════════════════════ -->
    <!-- ⑨ Drill-Down (KPI Snapshot is section ⑤ above)                       -->
    <!-- ════════════════════════════════════════════════════════════════════ -->
    @if (data.upsellKpis) {
      <!-- ⑨ Upsell Extension Offers — Drill-Down -->
      <div class="section-header upsell-section-header">
        <h4>⑨ Upsell Extension Offers — Drill-Down</h4>
        <p class="section-desc">Upsell data only. One row per extension offer. Headline KPIs are in section ⑤ above. Click Order # to open the reservation in admin.</p>
      </div>

      <!-- Upsell Drill-Down table -->
      <div class="card" id="upsell-detail-anchor">
        <div class="card-header">
          <h3>Upsell Drill-Down ({{ filteredUpsellRows.length }} of {{ data.upsellRows?.length ?? 0 }} rows)
            @if (activeUpsellFilter) {
              <span class="drill-badge">{{ activeUpsellFilter }} <button class="drill-clear" (click)="clearDrillDown('upsell')" title="Clear filter">✕</button></span>
            }
            <i class="pi pi-info-circle info-icon"
               pTooltip="Row-level view of every upsell offer in the window. Click Order # to open the admin order. Direction: NEXT = extend check-out, PREV = extend check-in. Guest Pays: actual charged amount for APPLIED offers; estimated (reservation rate × discount) for all others."
               tooltipPosition="top"></i>
          </h3>
          <span class="card-header-note">Click Order # to open the reservation in admin.</span>
        </div>
        <p-table #ptUpsell [value]="filteredUpsellRows" [paginator]="true" [rows]="25" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords} records"
          [scrollable]="true" scrollHeight="500px"
          styleClass="p-datatable-striped p-datatable-sm details-table"
          sortMode="single"
          [globalFilterFields]="['offerStatus','propertyCode','guestLastName','guestFirstName','direction','reservationId','orderId']">
          <ng-template pTemplate="header">
            <tr>
              <th class="num" pSortableColumn="orderId">Order #
                <i class="pi pi-info-circle info-icon" pTooltip="Links to the reservation in admin." tooltipPosition="top"></i>
                <p-sortIcon field="orderId"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="reservationId">Res ID
                <i class="pi pi-info-circle info-icon" pTooltip="str_reservation.reservationId — internal reservation identifier." tooltipPosition="top"></i>
                <p-sortIcon field="reservationId"></p-sortIcon>
              </th>
              <th pSortableColumn="offerStatus">Status <p-sortIcon field="offerStatus"></p-sortIcon></th>
              <th pSortableColumn="propertyCode">Property <p-sortIcon field="propertyCode"></p-sortIcon></th>
              <th pSortableColumn="guestLastName">Guest <p-sortIcon field="guestLastName"></p-sortIcon></th>
              <th pSortableColumn="checkInDate">Check-In <p-sortIcon field="checkInDate"></p-sortIcon></th>
              <th pSortableColumn="checkOutDate">Check-Out <p-sortIcon field="checkOutDate"></p-sortIcon></th>
              <th pSortableColumn="direction">Direction <p-sortIcon field="direction"></p-sortIcon></th>
              <th class="col-nights" pSortableColumn="originalNights">Orig Nights
                <i class="pi pi-info-circle info-icon" pTooltip="Number of nights in the original reservation (before any upsell extension)." tooltipPosition="top"></i>
                <p-sortIcon field="originalNights"></p-sortIcon>
              </th>
              <th pSortableColumn="offerDate">Offer Night
                <i class="pi pi-info-circle info-icon" pTooltip="The extra night(s) being offered. NEXT = night after current check-out. PREV = night before current check-in. Dual offers show both dates." tooltipPosition="top"></i>
                <p-sortIcon field="offerDate"></p-sortIcon>
              </th>
              <th pSortableColumn="createdDate">Sent <p-sortIcon field="createdDate"></p-sortIcon></th>
              <th class="num" pSortableColumn="effectivePct">Disc %
                <i class="pi pi-info-circle info-icon" pTooltip="Effective discount on the extra night's YieldMax base. May be lower than the season % if an owner floor cap was applied." tooltipPosition="top"></i>
                <p-sortIcon field="effectivePct"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="extraNightGuestCost">Guest Pays
                <i class="pi pi-info-circle info-icon"
                   pTooltip="For APPLIED offers: actual amount charged to the card. For all other statuses: estimated all-in cost using the reservation's own nightly rate with the promotion discount applied — (rate × (1−disc%) + fees) × (1+tax). For dual-direction non-applied offers the table shows ×2 (estimate per night). No YieldMax pricing used." tooltipPosition="top"></i>
                <p-sortIcon field="extraNightGuestCost"></p-sortIcon>
              </th>
              <th class="num" pSortableColumn="nightlyRateWithFeesAndTax">Curr. Rate/Night
                <i class="pi pi-info-circle info-icon"
                   pTooltip="Guest's all-in per-night cost for their existing reservation: (room + fees) × (1 + tax). Reference — compare against Guest Pays to see if the upsell is priced consistently." tooltipPosition="top"></i>
                <p-sortIcon field="nightlyRateWithFeesAndTax"></p-sortIcon>
              </th>
              <th pSortableColumn="acceptedDate">Guest Accepted
                <i class="pi pi-info-circle info-icon" pTooltip="When guest tapped Accept via SMS or web. If accepted but Guest Pays still shows an estimate, team still needs to apply." tooltipPosition="top"></i>
                <p-sortIcon field="acceptedDate"></p-sortIcon>
              </th>
            </tr>
            <tr class="filter-row">
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'orderId', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'reservationId', 'contains')"></th>
              <th><input pInputText type="text" placeholder="e.g. APPLIED" (input)="ptUpsell.filter($any($event.target).value, 'offerStatus', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'propertyCode', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'guestLastName', 'contains')"></th>
              <th><input pInputText type="text" placeholder="MM/DD/YY" (input)="ptUpsell.filter($any($event.target).value, 'checkInDate', 'contains')"></th>
              <th><input pInputText type="text" placeholder="MM/DD/YY" (input)="ptUpsell.filter($any($event.target).value, 'checkOutDate', 'contains')"></th>
              <th><input pInputText type="text" placeholder="NEXT / PREV" (input)="ptUpsell.filter($any($event.target).value, 'direction', 'contains')"></th>
              <th><input pInputText type="text" placeholder="e.g. 3" (input)="ptUpsell.filter($any($event.target).value, 'originalNights', 'equals')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'offerDate', 'contains')"></th>
              <th><input pInputText type="text" placeholder="Filter…" (input)="ptUpsell.filter($any($event.target).value, 'createdDate', 'contains')"></th>
              <!-- Disc%, Guest Pays, Curr. Rate/Night, Accepted — numeric/date, no text filter -->
              <th></th><th></th><th></th><th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="num">
                @if (row.orderId) {
                  <a href="https://admin.kbmhawaii.com/order/{{row.orderId}}"
                     target="_blank" rel="noopener noreferrer">{{ row.orderId }}</a>
                } @else { — }
              </td>
              <td class="num">{{ row.reservationId ?? '—' }}</td>
              <td>
                <span class="status-pill"
                      [class.qc]="row.offerStatus === 'APPLIED'"
                      [class.qs]="row.offerStatus === 'OFFERED' || row.offerStatus === 'ACCEPTED'"
                      [class.partial]="row.offerStatus === 'PARTIALLY_APPLIED'"
                      [class.qe]="row.offerStatus === 'EXPIRED'"
                      [class.qx]="row.offerStatus === 'DECLINED' || row.offerStatus === 'CANCELLED'"
                      [title]="row.offerStatus">
                  {{ row.offerStatus === 'PARTIALLY_APPLIED' ? 'Partial' : row.offerStatus }}
                </span>
              </td>
              <td>{{ row.propertyCode || '—' }}</td>
              <td>{{ row.guestFirstName }} {{ row.guestLastName }}</td>
              <td>{{ row.checkInDate  | date:'MM/dd/yy' }}</td>
              <td>{{ row.checkOutDate | date:'MM/dd/yy' }}</td>
              <td>
                @if (row.directions?.length > 1) {
                  <span style="color:#7c3aed;font-weight:600;" title="Both PREV (day before arrival) and NEXT (day after departure) offered">BOTH</span>
                } @else {
                  <span [style.color]="(row.direction ?? row.directions?.[0]) === 'NEXT' ? '#059669' : '#7c3aed'" style="font-weight:600;">
                    {{ row.direction ?? row.directions?.[0] ?? '—' }}
                  </span>
                }
              </td>
              <td class="col-nights">{{ row.originalNights ?? '—' }}</td>
              <td>
                @if (row.directions?.length > 1) {
                  <!-- Two lines — one per direction — so format and year match single-direction rows -->
                  <span style="font-size:11px;line-height:1.7;display:block;">
                    <span style="color:#6b7280;font-size:10px;margin-right:3px;">PREV</span>{{ row.prevDate | date:'MM/dd/yy' }}<br>
                    <span style="color:#6b7280;font-size:10px;margin-right:3px;">NEXT</span>{{ row.nextDate | date:'MM/dd/yy' }}
                  </span>
                } @else {
                  {{ row.offerDate | date:'MM/dd/yy' }}
                }
              </td>
              <td style="white-space:nowrap;">{{ row.createdDate | date:'MM/dd HH:mm':'America/New_York' }}</td>
              <td class="num">{{ row.effectivePct != null ? formatPct(row.effectivePct, true) : '—' }}</td>
              <td class="num">
                @if (row.extraNightGuestCost != null) {
                  @if (row.offerStatus === 'APPLIED') {
                    <!-- Actual charged amount from CC — ground truth, no estimation -->
                    {{ formatMoney(row.extraNightGuestCost) }}
                    <span style="font-size:10px;color:#059669;display:block;">actual charged</span>
                  } @else if (row.directions?.length > 1) {
                    <!-- Dual-direction, not yet applied — estimate × 2 nights -->
                    {{ formatMoney(row.extraNightGuestCost * 2) }}
                    <span style="font-size:10px;color:#6b7280;display:block;">×2 nights est.</span>
                  } @else {
                    <!-- Single-direction estimate -->
                    {{ formatMoney(row.extraNightGuestCost) }}
                    <span style="font-size:10px;color:#6b7280;display:block;">est.</span>
                  }
                } @else { — }
              </td>
              <td class="num">{{ row.nightlyRateWithFeesAndTax != null ? formatMoney(row.nightlyRateWithFeesAndTax) : '—' }}</td>
              <td style="white-space:nowrap;color:#6b7280;">{{ row.acceptedDate ? (row.acceptedDate | date:'MM/dd HH:mm':'America/New_York') : '—' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td colspan="8" style="font-style:italic;color:#6b7280">Total ({{ filteredUpsellRows.length }} rows)</td>
              <td class="col-nights num">{{ formatNumber(sumBy(filteredUpsellRows, 'originalNights')) }}</td>
              <td colspan="3"></td>
              <td class="num">{{ formatMoney(sumBy(filteredUpsellRows, 'extraNightGuestCost')) }}</td>
              <td colspan="2"></td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="15" class="text-center">No upsell offers in window.</td></tr>
          </ng-template>
        </p-table>
      </div>
    }

  }

  @if (!formLoading && data && data.kpis && data.kpis.quotesSent === 0 && (!data.holdKpis || data.holdKpis.holdsCreated === 0)) {
    <div class="empty-state">
      <i class="pi pi-inbox" style="font-size: 3rem; opacity: 0.4;"></i>
      <p>No quote or hold activity in this window. Try a wider date range or different filters.</p>
    </div>
  }
</div>
